import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  pipelineOrchestratorAPI,
  PipelineListParams,
  PipelineRunRequest,
  PipelineScheduleListParams,
  PipelineScheduleCreateRequest,
  PipelineScheduleUpdateRequest,
  PipelineScheduleWithEtag
} from '../services/pipelineOrchestrator';

// ============================================================================
// QUERY KEYS
// ============================================================================

export const pipelineKeys = {
  // Pipelines
  all: ['pipelines'] as const,
  list: (params?: PipelineListParams) => [...pipelineKeys.all, 'list', params] as const,
  detail: (id: string) => [...pipelineKeys.all, 'detail', id] as const,
  availableZones: (tenantId: string) => [...pipelineKeys.all, 'available-zones', tenantId] as const,

  // Schedules
  schedules: ['pipeline-schedules'] as const,
  schedulesList: (params?: PipelineScheduleListParams) =>
    [...pipelineKeys.schedules, 'list', params] as const,
  scheduleDetail: (id: string) => [...pipelineKeys.schedules, 'detail', id] as const
};

// ============================================================================
// PIPELINE HOOKS
// ============================================================================

/**
 * Hook to fetch pipeline runs with optional filters.
 * Auto-refreshes every 10s when there are running pipelines.
 */
export function usePipelines(params?: PipelineListParams) {
  return useQuery({
    queryKey: pipelineKeys.list(params),
    queryFn: () => pipelineOrchestratorAPI.listPipelines(params),
    staleTime: 60 * 1000, // 1 minute
    refetchOnWindowFocus: false,
    // The docstring above promised this from the start but it was never
    // implemented, so a running pipeline sat frozen in the list until the user
    // navigated away and back. Mirrors usePipeline's polling below; stops
    // entirely once nothing is in flight.
    refetchInterval: query => {
      const rows = query.state.data?.pipelines ?? [];
      const live = rows.some(p => p.status === 'running' || p.status === 'pending');
      return live ? 10000 : false;
    }
  });
}

/**
 * Hook to fetch a single pipeline detail.
 * Polls every 5s while the pipeline is running.
 */
export function usePipeline(id: string) {
  return useQuery({
    queryKey: pipelineKeys.detail(id),
    queryFn: () => pipelineOrchestratorAPI.getPipeline(id),
    enabled: !!id,
    staleTime: 5 * 1000, // 5 seconds for live data
    refetchInterval: query => {
      const status = query.state.data?.status;
      return status === 'running' || status === 'pending' ? 5000 : false;
    }
  });
}

/**
 * Hook to start a new pipeline run.
 */
export function useRunPipeline() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: PipelineRunRequest) => pipelineOrchestratorAPI.runPipeline(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: pipelineKeys.all });
    }
  });
}

/**
 * Hook to cancel a running pipeline.
 */
export function useCancelPipeline() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => pipelineOrchestratorAPI.cancelPipeline(id),
    onSuccess: (_data, id) => {
      queryClient.invalidateQueries({ queryKey: pipelineKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: pipelineKeys.all });
    }
  });
}

/**
 * Hook to retry failed pipeline steps.
 */
export function useRetryPipeline() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => pipelineOrchestratorAPI.retryPipeline(id),
    onSuccess: (_data, id) => {
      queryClient.invalidateQueries({ queryKey: pipelineKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: pipelineKeys.all });
    }
  });
}

/**
 * Hook to fetch available zones for a tenant (used in pipeline creation form).
 * Zones are static config — cached for 10 minutes.
 */
export function useAvailableZones(tenantId: string) {
  return useQuery({
    queryKey: pipelineKeys.availableZones(tenantId),
    queryFn: () => pipelineOrchestratorAPI.getAvailableZones(tenantId),
    enabled: !!tenantId,
    staleTime: 10 * 60 * 1000, // 10 minutes
    refetchOnWindowFocus: false
  });
}

/**
 * Hook to fetch event logs for a specific pipeline step.
 *
 * Two-source architecture:
 * - Running steps: live logs arrive via Supabase broadcast (step_log event)
 *   and are written to this same cache key by usePipelineRealtime. No API call.
 * - Completed steps: fetched once from GCS via GET /pipelines/{id}/steps/{stepId}/logs.
 *
 * Both sources share the same React Query cache key, so the viewer is source-agnostic.
 */
export function useStepLogs(
  pipelineId: string | undefined,
  stepId: string | undefined,
  stepStatus?: string
) {
  const isLive = stepStatus === 'running' || stepStatus === 'pending';

  return useQuery({
    queryKey: [...pipelineKeys.all, 'step-logs', pipelineId, stepId],
    queryFn: () => pipelineOrchestratorAPI.getStepLogs(pipelineId!, stepId!),
    // Only fetch from API for completed steps — live steps get data via realtime
    enabled: !!pipelineId && !!stepId && !isLive,
    staleTime: 60 * 1000, // Historical logs from GCS don't change
    refetchOnWindowFocus: false
  });
}

// ============================================================================
// PIPELINE SCHEDULE HOOKS
// ============================================================================

/**
 * Hook to fetch pipeline schedules with optional filters.
 */
export function usePipelineSchedules(params?: PipelineScheduleListParams) {
  return useQuery({
    queryKey: pipelineKeys.schedulesList(params),
    queryFn: () => pipelineOrchestratorAPI.listSchedules(params),
    staleTime: 5 * 60 * 1000, // 5 minutes
    refetchOnWindowFocus: false
  });
}

/**
 * Hook to fetch a single pipeline schedule. The cached value includes the ETag,
 * which useUpdatePipelineSchedule reads to send If-Match on writes.
 */
export function usePipelineSchedule(id: string) {
  return useQuery({
    queryKey: pipelineKeys.scheduleDetail(id),
    queryFn: () => pipelineOrchestratorAPI.getSchedule(id),
    enabled: !!id,
    staleTime: 5 * 60 * 1000
  });
}

/**
 * Hook to create a new pipeline schedule.
 * Pass `idempotencyKey` (e.g. crypto.randomUUID()) to harden against double-submits.
 */
export function useCreatePipelineSchedule() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      data,
      idempotencyKey
    }: {
      data: PipelineScheduleCreateRequest;
      idempotencyKey?: string;
    }) => pipelineOrchestratorAPI.createSchedule(data, idempotencyKey),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: pipelineKeys.schedules });
    }
  });
}

/**
 * Hook to update an existing pipeline schedule.
 * Automatically forwards the cached ETag as If-Match if present.
 */
export function useUpdatePipelineSchedule() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, ...data }: PipelineScheduleUpdateRequest & { id: string }) => {
      const cached = queryClient.getQueryData<PipelineScheduleWithEtag>(
        pipelineKeys.scheduleDetail(id)
      );
      const ifMatch = cached?.etag ?? undefined;
      return pipelineOrchestratorAPI.updateSchedule(id, data, ifMatch);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: pipelineKeys.schedules });
    }
  });
}

/**
 * Hook to delete a pipeline schedule.
 */
export function useDeletePipelineSchedule() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => pipelineOrchestratorAPI.deleteSchedule(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: pipelineKeys.schedules });
    }
  });
}

/**
 * Hook to manually trigger a pipeline schedule.
 * Pass an `idempotencyKey` per user-initiated click (e.g. crypto.randomUUID())
 * so React Query retries dedup but distinct user clicks don't.
 */
export function useTriggerPipelineSchedule() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, idempotencyKey }: { id: string; idempotencyKey?: string }) =>
      pipelineOrchestratorAPI.triggerSchedule(id, idempotencyKey),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: pipelineKeys.schedules });
      queryClient.invalidateQueries({ queryKey: pipelineKeys.all });
    }
  });
}
